import { useEffect, useState, useMemo } from "react";
import { useDepositSettings } from "@/hooks/useDepositSettings";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, DollarSign, TrendingUp, TrendingDown, CalendarDays, Clock, ChevronDown, Plus, Trash2, Receipt, Building, BarChart3, Pencil, Check, X } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { format, subMonths, startOfMonth, endOfMonth } from "date-fns";
import { toast } from "sonner";
import { isTestAccount } from "@/lib/testAccounts";

type Expense = {
  id: string;
  category: string;
  description: string;
  amount: number;
  expense_date: string;
  created_at: string;
};

const EXPENSE_CATEGORIES = [
  { value: "cogs", label: "Cost of Goods (COGS)" },
  { value: "rent", label: "Rent" },
  { value: "utilities", label: "Utilities" },
  { value: "supplies", label: "Supplies" },
  { value: "marketing", label: "Marketing" },
  { value: "software", label: "Software/Subscriptions" },
  { value: "insurance", label: "Insurance" },
  { value: "other", label: "Other" },
];

const Revenue = () => {
  const navigate = useNavigate();
  const { depositAmount: depositPerBooking } = useDepositSettings();
  const [bookings, setBookings] = useState<any[]>([]);
  const [profiles, setProfiles] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [showExpenses, setShowExpenses] = useState(false);
  const [monthlyRent, setMonthlyRent] = useState("");
  const [rentSaved, setRentSaved] = useState(false);

  // New expense form
  const [newCategory, setNewCategory] = useState("cogs");
  const [newDescription, setNewDescription] = useState("");
  const [newAmount, setNewAmount] = useState("");
  const [newDate, setNewDate] = useState(format(new Date(), "yyyy-MM-dd"));

  // Editing expense state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editCategory, setEditCategory] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editAmount, setEditAmount] = useState("");
  const [editDate, setEditDate] = useState("");

  useEffect(() => {
    const fetchAll = async () => {
      const [bookingsRes, expensesRes, rentRes] = await Promise.all([
        supabase.from("bookings").select("*").is("deleted_at", null).order("booking_date", { ascending: false }),
        supabase.from("expenses").select("*").order("expense_date", { ascending: false }),
        supabase.from("site_settings").select("value").eq("key", "monthly_rent").maybeSingle(),
      ]);

      const allBookings = (bookingsRes.data || []).filter((b: any) => !isTestAccount(b.user_id));
      setBookings(allBookings);
      setExpenses((expensesRes.data as Expense[]) || []);
      if (rentRes.data?.value) setMonthlyRent(rentRes.data.value);

      const userIds = [...new Set(allBookings.map((b: any) => b.user_id))];
      if (userIds.length > 0) {
        const { data: pd } = await supabase
          .from("profiles")
          .select("user_id, full_name")
          .in("user_id", userIds);
        const map: Record<string, string> = {};
        (pd || []).forEach((p) => { map[p.user_id] = p.full_name || "Unknown"; });
        setProfiles(map);
      }
      setLoading(false);
    };
    fetchAll();
  }, []);

  const saveRent = async () => {
    const { error } = await supabase
      .from("site_settings")
      .upsert({ key: "monthly_rent", value: monthlyRent }, { onConflict: "key" });
    if (error) toast.error("Failed to save rent");
    else { toast.success("Monthly rent saved"); setRentSaved(true); setTimeout(() => setRentSaved(false), 2000); }
  };

  const addExpense = async () => {
    if (!newAmount || Number(newAmount) <= 0) { toast.error("Enter a valid amount"); return; }
    const { data, error } = await supabase
      .from("expenses")
      .insert({ category: newCategory, description: newDescription, amount: Number(newAmount), expense_date: newDate })
      .select()
      .single();
    if (error) { toast.error("Failed to add expense"); return; }
    setExpenses((prev) => [data as Expense, ...prev]);
    setNewDescription("");
    setNewAmount("");
    toast.success("Expense added");
  };

  const deleteExpense = async (id: string) => {
    const { error } = await supabase.from("expenses").delete().eq("id", id);
    if (error) { toast.error("Failed to delete"); return; }
    setExpenses((prev) => prev.filter((e) => e.id !== id));
    toast.success("Expense deleted");
  };

  const startEditing = (e: Expense) => {
    setEditingId(e.id);
    setEditCategory(e.category);
    setEditDescription(e.description);
    setEditAmount(String(e.amount));
    setEditDate(e.expense_date);
  };

  const cancelEditing = () => setEditingId(null);

  const saveEditing = async () => {
    if (!editingId || !editAmount || Number(editAmount) <= 0) { toast.error("Enter a valid amount"); return; }
    const { error } = await supabase
      .from("expenses")
      .update({ category: editCategory, description: editDescription, amount: Number(editAmount), expense_date: editDate })
      .eq("id", editingId);
    if (error) { toast.error("Failed to update expense"); return; }
    setExpenses((prev) => prev.map((e) => e.id === editingId ? { ...e, category: editCategory, description: editDescription, amount: Number(editAmount), expense_date: editDate } : e));
    setEditingId(null);
    toast.success("Expense updated");
  };

  // Parse duration string to hours
  const parseDurationToHours = (dur: string): number => {
    let hours = 0;
    const hrMatch = dur.match(/(\d+)\s*hr/i);
    const minMatch = dur.match(/(\d+)\s*min/i);
    if (hrMatch) hours += parseInt(hrMatch[1]);
    if (minMatch) hours += parseInt(minMatch[1]) / 60;
    return hours || 0.5;
  };

  if (loading) return <div className="text-muted-foreground text-sm">Loading revenue data…</div>;

  const confirmed = bookings.filter((b) => b.status === "confirmed" || b.status === "completed");
  const cancelled = bookings.filter((b) => b.status === "cancelled");

  const totalRevenue = confirmed.reduce((sum, b) => sum + Number(b.price), 0);
  const totalDiscounts = bookings.reduce((sum, b) => sum + Number(b.discount_applied || 0), 0);
  const totalDeposits = bookings.length * depositPerBooking;
  const cancellationFees = cancelled.length * depositPerBooking;
  const grossRevenue = totalRevenue - totalDiscounts + cancellationFees;

  // Expenses calculations
  const totalExpenses = expenses.reduce((sum, e) => sum + Number(e.amount), 0);
  const rentAmount = Number(monthlyRent) || 0;
  // Count distinct months with bookings for rent calculation
  const distinctMonths = new Set(confirmed.map((b) => b.booking_date.substring(0, 7))).size;
  const totalRent = rentAmount * Math.max(distinctMonths, 1);

  // COGS breakdown
  const cogsByCategory: Record<string, number> = {};
  expenses.forEach((e) => {
    cogsByCategory[e.category] = (cogsByCategory[e.category] || 0) + Number(e.amount);
  });

  const totalCOGS = totalExpenses + totalRent;
  const netProfit = grossRevenue - totalCOGS;

  // Monthly breakdown
  const now = new Date();
  const months = Array.from({ length: 6 }, (_, i) => {
    const d = subMonths(now, i);
    const start = startOfMonth(d);
    const end = endOfMonth(d);
    const monthKey = format(d, "yyyy-MM");
    const monthBookings = confirmed.filter((b) => {
      const bd = new Date(b.booking_date + "T12:00:00");
      return bd >= start && bd <= end;
    });
    const monthCancelled = cancelled.filter((b) => {
      const bd = new Date(b.booking_date + "T12:00:00");
      return bd >= start && bd <= end;
    });
    const monthExpenses = expenses.filter((e) => e.expense_date.startsWith(monthKey));
    const monthExpenseTotal = monthExpenses.reduce((s, e) => s + Number(e.amount), 0);
    const monthHours = monthBookings.reduce((s, b) => s + parseDurationToHours(b.duration || "30 min"), 0);
    const monthRevNet = monthBookings.reduce((s, b) => s + Number(b.price), 0)
      - monthBookings.reduce((s, b) => s + Number(b.discount_applied || 0), 0)
      + monthCancelled.length * depositPerBooking
      - (monthExpenseTotal + rentAmount);
    return {
      label: format(d, "MMM yyyy"),
      shortLabel: format(d, "MMM"),
      revenue: monthBookings.reduce((s, b) => s + Number(b.price), 0),
      discounts: monthBookings.reduce((s, b) => s + Number(b.discount_applied || 0), 0),
      cancellationFees: monthCancelled.length * depositPerBooking,
      bookingCount: monthBookings.length,
      cancelledCount: monthCancelled.length,
      expenses: monthExpenseTotal + rentAmount,
      hours: monthHours,
      profitPerHour: monthHours > 0 ? monthRevNet / monthHours : 0,
    };
  });

  // Chart data (reversed so oldest is first)
  const chartData = [...months].reverse().map((m) => ({
    month: m.shortLabel,
    profitPerHour: Number(m.profitPerHour.toFixed(2)),
    hours: Number(m.hours.toFixed(1)),
  }));


  // Service breakdown with hours
  const serviceMap: Record<string, { revenue: number; count: number; totalHours: number }> = {};
  confirmed.forEach((b) => {
    if (!serviceMap[b.service_name]) serviceMap[b.service_name] = { revenue: 0, count: 0, totalHours: 0 };
    serviceMap[b.service_name].revenue += Number(b.price);
    serviceMap[b.service_name].count += 1;
    serviceMap[b.service_name].totalHours += parseDurationToHours(b.duration || "30 min");
  });
  const serviceBreakdown = Object.entries(serviceMap)
    .sort(([, a], [, b]) => b.revenue - a.revenue);

  const totalHours = confirmed.reduce((sum, b) => sum + parseDurationToHours(b.duration || "30 min"), 0);
  const avgProfitPerHour = totalHours > 0 ? netProfit / totalHours : 0;

  return (
    <div className="space-y-6 sm:space-y-8 px-1 sm:px-0">
      <div className="flex items-center gap-3 sm:gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate("/admin")} className="gap-1 text-muted-foreground shrink-0">
          <ArrowLeft size={14} /> Back
        </Button>
        <div className="min-w-0">
          <h1 className="font-serif text-2xl sm:text-3xl font-bold italic">Revenue Summary</h1>
          <p className="text-muted-foreground text-xs sm:text-sm mt-1">Complete financial breakdown</p>
        </div>
      </div>

      {/* Top-line stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-4">
        {[
          { label: "Gross Revenue", value: `$${grossRevenue.toLocaleString()}`, icon: DollarSign, highlight: true },
          { label: "Discounts", value: `-$${totalDiscounts.toLocaleString()}`, icon: TrendingDown },
          { label: "Cancel Fees", value: `+$${cancellationFees.toLocaleString()}`, icon: TrendingUp },
          { label: "Total Deposits", value: `$${totalDeposits.toLocaleString()}`, icon: DollarSign },
          { label: "Total Expenses", value: `-$${totalCOGS.toLocaleString()}`, icon: Receipt, highlight: showExpenses },
          { label: "Net Profit", value: `$${netProfit.toLocaleString()}`, icon: DollarSign, highlight: true },
        ].map((s) => (
          <Card key={s.label} className={`bg-card border-border/50 ${s.highlight ? "border-primary/30" : ""}`}>
            <CardHeader className="flex flex-row items-center justify-between pb-1 sm:pb-2 px-3 sm:px-6 pt-3 sm:pt-6">
              <CardTitle className="text-[9px] sm:text-[10px] font-light tracking-[0.15em] sm:tracking-[0.2em] uppercase text-muted-foreground truncate">
                {s.label}
              </CardTitle>
              <s.icon size={12} className="text-primary shrink-0 sm:hidden" />
              <s.icon size={14} className="text-primary shrink-0 hidden sm:block" />
            </CardHeader>
            <CardContent className="px-3 sm:px-6 pb-3 sm:pb-6">
              <p className="text-lg sm:text-xl font-bold">{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Expenses Toggle & Management */}
      <Card className="bg-card border-border/50">
        <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <CardTitle className="text-sm font-light tracking-[0.3em] uppercase text-muted-foreground flex items-center gap-2">
            <Receipt size={16} className="text-primary" /> Expenses & COGS
          </CardTitle>
          <div className="flex items-center gap-2">
            <Label htmlFor="expense-toggle" className="text-xs text-muted-foreground">Track Expenses</Label>
            <Switch id="expense-toggle" checked={showExpenses} onCheckedChange={setShowExpenses} />
          </div>
        </CardHeader>

        {showExpenses && (
          <CardContent className="space-y-6">
            {/* Monthly Rent */}
            <div className="flex flex-col sm:flex-row sm:items-end gap-3 p-4 rounded-lg bg-muted/30 border border-border/50">
              <Building size={18} className="text-primary hidden sm:block mb-2" />
              <div className="flex-1 space-y-1">
                <Label className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <Building size={14} className="text-primary sm:hidden" /> Monthly Rent (fixed)
                </Label>
                <Input
                  type="number"
                  placeholder="0.00"
                  value={monthlyRent}
                  onChange={(e) => setMonthlyRent(e.target.value)}
                  className="max-w-full sm:max-w-[180px]"
                />
              </div>
              <Button size="sm" variant="outline" onClick={saveRent} className="w-full sm:w-auto">
                {rentSaved ? "Saved ✓" : "Save"}
              </Button>
            </div>

            {/* Add Expense Form */}
            <div className="space-y-3">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Add Expense</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <Select value={newCategory} onValueChange={setNewCategory}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPENSE_CATEGORIES.filter((c) => c.value !== "rent").map((c) => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  placeholder="Description"
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                />
                <Input
                  type="number"
                  placeholder="Amount"
                  value={newAmount}
                  onChange={(e) => setNewAmount(e.target.value)}
                />
                <div className="flex gap-2">
                  <Input
                    type="date"
                    value={newDate}
                    onChange={(e) => setNewDate(e.target.value)}
                    className="flex-1"
                  />
                  <Button size="icon" onClick={addExpense} className="shrink-0">
                    <Plus size={16} />
                  </Button>
                </div>
              </div>
            </div>

            {/* COGS Breakdown */}
            {Object.keys(cogsByCategory).length > 0 && (
              <Collapsible>
                <CollapsibleTrigger className="w-full">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-border/50 hover:bg-muted/50 transition-colors">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">COGS Breakdown</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold">${totalExpenses.toLocaleString()}</span>
                      <ChevronDown size={14} className="text-muted-foreground" />
                    </div>
                  </div>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="mt-2 space-y-1">
                    {Object.entries(cogsByCategory).sort(([, a], [, b]) => b - a).map(([cat, amount]) => {
                      const catLabel = EXPENSE_CATEGORIES.find((c) => c.value === cat)?.label || cat;
                      const pct = totalCOGS > 0 ? ((amount / totalCOGS) * 100).toFixed(1) : "0";
                      return (
                        <div key={cat} className="flex items-center justify-between p-2 text-sm">
                          <span className="text-muted-foreground">{catLabel}</span>
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-muted-foreground">{pct}%</span>
                            <span className="font-medium">${amount.toLocaleString()}</span>
                          </div>
                        </div>
                      );
                    })}
                    {rentAmount > 0 && (
                      <div className="flex items-center justify-between p-2 text-sm border-t border-border/50">
                        <span className="text-muted-foreground">Rent ({distinctMonths} mo)</span>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-muted-foreground">
                            {totalCOGS > 0 ? ((totalRent / totalCOGS) * 100).toFixed(1) : "0"}%
                          </span>
                          <span className="font-medium">${totalRent.toLocaleString()}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            )}

            {/* Expense List */}
            {expenses.length > 0 && (
              <div className="max-h-60 overflow-y-auto overflow-x-auto -mx-2 sm:mx-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Date</TableHead>
                      <TableHead className="text-xs hidden sm:table-cell">Category</TableHead>
                      <TableHead className="text-xs hidden sm:table-cell">Description</TableHead>
                      <TableHead className="text-xs">Amount</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {expenses.map((e) => (
                      <TableRow key={e.id}>
                        {editingId === e.id ? (
                          <>
                            <TableCell>
                              <Input type="date" value={editDate} onChange={(ev) => setEditDate(ev.target.value)} className="h-8 text-xs sm:text-sm w-[110px] sm:w-[130px]" />
                            </TableCell>
                            <TableCell className="hidden sm:table-cell">
                              <Select value={editCategory} onValueChange={setEditCategory}>
                                <SelectTrigger className="h-8 text-sm w-[140px]"><SelectValue /></SelectTrigger>
                                <SelectContent>{EXPENSE_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell className="hidden sm:table-cell">
                              <Input value={editDescription} onChange={(ev) => setEditDescription(ev.target.value)} className="h-8 text-sm" placeholder="Description" />
                            </TableCell>
                            <TableCell>
                              <Input type="number" value={editAmount} onChange={(ev) => setEditAmount(ev.target.value)} className="h-8 text-xs sm:text-sm w-[80px] sm:w-[90px]" min="0" step="0.01" />
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                <Button variant="ghost" size="icon" className="h-7 w-7 text-primary" onClick={saveEditing}><Check size={14} /></Button>
                                <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground" onClick={cancelEditing}><X size={14} /></Button>
                              </div>
                            </TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell className="text-xs sm:text-sm">
                              <div>{format(new Date(e.expense_date), "MMM d")}</div>
                              <div className="text-[10px] text-muted-foreground sm:hidden">
                                {EXPENSE_CATEGORIES.find((c) => c.value === e.category)?.label || e.category}
                              </div>
                            </TableCell>
                            <TableCell className="text-sm hidden sm:table-cell">
                              {EXPENSE_CATEGORIES.find((c) => c.value === e.category)?.label || e.category}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground hidden sm:table-cell">{e.description || "—"}</TableCell>
                            <TableCell className="text-xs sm:text-sm font-medium">${Number(e.amount).toLocaleString()}</TableCell>
                            <TableCell>
                              <div className="flex gap-0.5 sm:gap-1">
                                <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-primary" onClick={() => startEditing(e)}>
                                  <Pencil size={13} />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => deleteExpense(e.id)}>
                                  <Trash2 size={13} />
                                </Button>
                              </div>
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Profit Per Hour - Expandable */}
      <Collapsible>
        <Card className="bg-card border-border/50 border-primary/30">
          <CollapsibleTrigger className="w-full text-left">
            <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-2">
              <CardTitle className="text-xs sm:text-sm font-light tracking-[0.2em] sm:tracking-[0.3em] uppercase text-muted-foreground flex items-center gap-2">
                <Clock size={16} className="text-primary" /> Avg Profit Per Hour
              </CardTitle>
              <div className="flex items-center gap-3">
                <span className="text-xl sm:text-2xl font-bold">${avgProfitPerHour.toFixed(2)}/hr</span>
                <ChevronDown size={16} className="text-muted-foreground transition-transform duration-200 [[data-state=open]_&]:rotate-180" />
              </div>
            </CardHeader>
            <CardContent className="pb-2">
              <p className="text-[11px] sm:text-xs text-muted-foreground">
                {totalHours.toFixed(1)} total hours worked · {confirmed.length} bookings · Click to expand
              </p>
            </CardContent>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="p-0 pt-2 border-t border-border/50 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Service</TableHead>
                    <TableHead className="text-xs">Bookings</TableHead>
                    <TableHead className="text-xs hidden sm:table-cell">Hours</TableHead>
                    <TableHead className="text-xs">Revenue</TableHead>
                    <TableHead className="text-xs">$/Hr</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {serviceBreakdown.map(([name, data]) => {
                    const perHour = data.totalHours > 0 ? data.revenue / data.totalHours : 0;
                    return (
                      <TableRow key={name}>
                        <TableCell className="font-medium text-xs sm:text-sm max-w-[120px] truncate">{name}</TableCell>
                        <TableCell className="text-xs sm:text-sm">{data.count}</TableCell>
                        <TableCell className="text-sm hidden sm:table-cell">{data.totalHours.toFixed(1)}</TableCell>
                        <TableCell className="text-xs sm:text-sm">${data.revenue.toLocaleString()}</TableCell>
                        <TableCell className={`text-xs sm:text-sm font-semibold ${perHour >= avgProfitPerHour ? "text-primary" : "text-destructive"}`}>
                          ${perHour.toFixed(0)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Profit Per Hour Trend Chart */}
      <Card className="bg-card border-border/50">
        <CardHeader>
          <CardTitle className="text-sm font-light tracking-[0.3em] uppercase text-muted-foreground flex items-center gap-2">
            <BarChart3 size={16} className="text-primary" /> Profit/Hr Trend (6 Months)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[220px] sm:h-[280px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 10, right: 5, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="profitGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v}`} width={45} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "8px",
                    fontSize: "12px",
                  }}
                  formatter={(value: number, name: string) => [
                    name === "profitPerHour" ? `$${value.toFixed(2)}/hr` : `${value} hrs`,
                    name === "profitPerHour" ? "Profit/Hr" : "Hours Worked",
                  ]}
                />
                <ReferenceLine y={avgProfitPerHour} stroke="hsl(var(--muted-foreground))" strokeDasharray="4 4" label={{ value: `Avg $${avgProfitPerHour.toFixed(0)}`, position: "insideTopRight", fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
                <Area type="monotone" dataKey="profitPerHour" stroke="hsl(var(--primary))" fill="url(#profitGradient)" strokeWidth={2.5} dot={{ r: 3, fill: "hsl(var(--primary))", strokeWidth: 2, stroke: "hsl(var(--background))" }} activeDot={{ r: 5 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-6 mt-3 text-[10px] sm:text-xs text-muted-foreground">
            {chartData.map((d) => (
              <span key={d.month}>{d.month}: {d.hours}h</span>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Monthly Breakdown */}
      <Card className="bg-card border-border/50">
        <CardHeader>
          <CardTitle className="text-sm font-light tracking-[0.3em] uppercase text-muted-foreground flex items-center gap-2">
            <CalendarDays size={16} className="text-primary" /> Monthly Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Month</TableHead>
                <TableHead className="text-xs">Bookings</TableHead>
                <TableHead className="text-xs hidden sm:table-cell">Cancelled</TableHead>
                <TableHead className="text-xs">Revenue</TableHead>
                <TableHead className="text-xs hidden sm:table-cell">Discounts</TableHead>
                <TableHead className="text-xs hidden lg:table-cell">Cancel Fees</TableHead>
                {showExpenses && <TableHead className="text-xs hidden sm:table-cell">Expenses</TableHead>}
                <TableHead className="text-xs">Net</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {months.map((m) => {
                const monthNet = m.revenue - m.discounts + m.cancellationFees - (showExpenses ? m.expenses : 0);
                return (
                  <TableRow key={m.label}>
                    <TableCell className="font-medium text-xs sm:text-sm whitespace-nowrap">{m.label}</TableCell>
                    <TableCell className="text-xs sm:text-sm">{m.bookingCount}</TableCell>
                    <TableCell className="text-xs sm:text-sm text-destructive hidden sm:table-cell">{m.cancelledCount}</TableCell>
                    <TableCell className="text-xs sm:text-sm">${m.revenue.toLocaleString()}</TableCell>
                    <TableCell className="text-xs sm:text-sm text-muted-foreground hidden sm:table-cell">-${m.discounts.toLocaleString()}</TableCell>
                    <TableCell className="text-xs sm:text-sm text-primary hidden lg:table-cell">+${m.cancellationFees.toLocaleString()}</TableCell>
                    {showExpenses && <TableCell className="text-xs sm:text-sm text-destructive hidden sm:table-cell">-${m.expenses.toLocaleString()}</TableCell>}
                    <TableCell className={`text-xs sm:text-sm font-semibold ${monthNet >= 0 ? "text-primary" : "text-destructive"}`}>
                      ${monthNet.toLocaleString()}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Service Breakdown */}
      <Card className="bg-card border-border/50">
        <CardHeader>
          <CardTitle className="text-sm font-light tracking-[0.3em] uppercase text-muted-foreground flex items-center gap-2">
            <TrendingUp size={16} className="text-primary" /> Revenue by Service
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Service</TableHead>
                <TableHead>Bookings</TableHead>
                <TableHead>Hours</TableHead>
                <TableHead>Revenue</TableHead>
                <TableHead>Avg Price</TableHead>
                <TableHead>$/Hour</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {serviceBreakdown.map(([name, data]) => {
                const perHour = data.totalHours > 0 ? data.revenue / data.totalHours : 0;
                return (
                  <TableRow key={name}>
                    <TableCell className="font-medium text-sm">{name}</TableCell>
                    <TableCell className="text-sm">{data.count}</TableCell>
                    <TableCell className="text-sm">{data.totalHours.toFixed(1)}</TableCell>
                    <TableCell className="text-sm">${data.revenue.toLocaleString()}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">${Math.round(data.revenue / data.count)}</TableCell>
                    <TableCell className={`text-sm font-semibold ${perHour >= avgProfitPerHour ? "text-primary" : "text-destructive"}`}>
                      ${perHour.toFixed(2)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default Revenue;
